import { useEffect, useRef, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useRecurly } from "@recurly/react-recurly";
import cx from "classnames";

import t from "../../../utils/translate";
import routes from "../../../routes";
import SiteWrapper from "../../SiteWrapper";
import Column from "../../Column";
import PaymentForm from "./componets/PaymentForm";
import useAppContext from "../../AppContext";
import RoundButton from "../../RoundButton";
import Terms from "./componets/Terms";
import SequenceFooter from "../../SequenceFooter";
import NewPricing from "./componets/NewPricing";
import {
  B2B,
  B2C,
  COMPOUND,
  DURATION_COUNT,
  OFFER_PLAN_NAME,
  RECURLY
} from "../constants";
import useCreateClinicSub from "../../../utils/useCreateClinicSub";
import { useCompoundEligibility } from "../../../utils/useCompoundEligibility";
import { runValidators } from "../../../utils/useDistributedValidator";
import {
  dispatchEvent,
  setPageData,
  signupPageView
} from "../../../utils/analytics";
import { setCookieValue, SECONDS_IN_THIRTY_DAYS } from "../../../utils/cookies";
import getSessionData from "../../../utils/getSessionData";
import useDelay from "../../../utils/useDelay";

import styles from "./styles.module.scss";

const Payment = () => {
  const history = useHistory();
  const location = useLocation();
  const recurly = useRecurly();
  const { delay } = useDelay();

  const { profile = {}, newQuiz, authData } = useAppContext();
  const isUni3or4Quiz = newQuiz.isUni3 || newQuiz.isUni4;
  const { compoundEligible } = useCompoundEligibility();

  const {
    create,
    getRedirectUrl,
    isError: enrollmentError,
    isLoading: enrolling,
    errorMessaging,
    errors
  } = useCreateClinicSub();
  const [error, setError] = useState();
  const [agreement, setAgreement] = useState(false);

  const {
    firstName = "",
    lastName = "",
    mailingAddress = {},
    medical = {}
  } = profile;

  const formRef = useRef(null);
  const enrollmentData = useRef(null);
  const [isCardElementValid, setIsCardElementValid] = useState(false);
  const [isCardElementTouched, setIsCardElementTouched] = useState(false);
  const [isBillingInfoValid, setIsBillingInfoValid] = useState(true);
  const [isCheckoutBtnTouched, setIsCheckoutBtnTouched] = useState(false);
  const [data, setData] = useState({
    firstName,
    lastName,
    street1: mailingAddress.addressLine1,
    street2: mailingAddress.addressLine2,
    city: mailingAddress.city,
    state: mailingAddress.state,
    postalCode: mailingAddress.zipcode,
    phone: profile.phoneNumber,
    country: mailingAddress.country,
    couponCode: ""
  });
  const [plan, setPlan] = useState(location.state?.plan || {});
  const [couponCodeDiscount, setCouponCodeDiscount] = useState({
    discount: "",
    total: "",
    terms: ""
  });

  const { treatment } = location.state || {};

  const handleData = (key, value) => {
    setData({ ...data, [key]: value });
  };

  const changePlan = () => {
    history.push(routes.clinicCheckoutTreatmentOptions);
  };

  useEffect(() => {
    window.newrelic?.noticeError(enrollmentError || error);
  }, [enrollmentError, error]);

  useEffect(() => {
    setPageData({
      page_category: "clinic",
      site_section: "clinical",
      ...getPlanAnalyticsData(plan)
    });
    signupPageView("payment");
  }, []);

  const onAgreement = e => {
    setAgreement(e.target.checked);
  };

  const onFormBlur = () => {
    setIsBillingInfoValid(runValidators());
  };

  if (!plan || !Object.keys(plan)?.length) {
    changePlan();
    return null;
  }

  const { clinical } = plan;

  const handleSequenceRedirect = async () => {
    if (enrollmentData.current) {
      const redirectUrl = await getRedirectUrl(enrollmentData.current);
      if (redirectUrl) {
        setCookieValue(
          "ww_sequence_redirect_url",
          redirectUrl,
          SECONDS_IN_THIRTY_DAYS,
          "/",
          false
        );

        window.location.href = redirectUrl;
      }
    }
  };

  const enrollUser = async (err, token) => {
    if (err) {
      setError(err.message);
    } else {
      const enrollData = await create({
        data: { ...data, token: token.id },
        plan
      });

      if (!enrollData) {
        return;
      }

      enrollmentData.current = enrollData;

      setPageData({
        ...getPostPurchaseAnalyticsData(enrollData),
        ...getUserAndAddressData({
          ...data,
          email: medical?.email
        })
      });

      dispatchEvent({
        event: "non_click_event",
        event_key: "sign:purchase",
        event_category: "signup",
        event_action: "purchase",
        event_label: ""
      });

      /**
       * Testing the redirect delay for MKT
       */
      await delay(1000);

      handleSequenceRedirect();
    }
  };

  const submitPaymentInfo = () => {
    setError(null);

    if (enrollmentData.current) {
      handleSequenceRedirect();
      return;
    }
    const isValid = runValidators();
    if (isCardElementValid && isValid && agreement) {
      recurly.token(formRef.current, enrollUser);
    }
  };

  const isCheckoutBtnDisabled =
    !isBillingInfoValid ||
    !isCardElementValid ||
    !isCardElementTouched ||
    !agreement ||
    errors?.createSequenceUserIsError ||
    errors?.getSequenceRedirectUrlIsError;

  const handleOverlayClick = () => {
    setIsCheckoutBtnTouched(true);
  };

  return (
    <SiteWrapper size="small">
      <Column className={styles.content}>
        <NewPricing
          plan={plan}
          setPlan={setPlan}
          data={data}
          handleData={handleData}
          couponCodeDiscount={couponCodeDiscount}
          setCouponCodeDiscount={setCouponCodeDiscount}
          clinical={clinical}
          isUni3or4Quiz={isUni3or4Quiz}
        />
        {!authData?.loggedInAs &&
          (enrollmentError || error) &&
          errorMessaging && (
            <div>
              <p className={styles.preError}>{errorMessaging}</p>
            </div>
          )}
        <form ref={formRef} onBlur={onFormBlur}>
          <PaymentForm
            data={data}
            plan={plan}
            onChange={handleData}
            setDiscount={setCouponCodeDiscount}
            isCardElementTouched={isCardElementTouched}
            isCheckoutBtnTouched={isCheckoutBtnTouched}
            setIsCardElementValid={setIsCardElementValid}
            setIsBillingInfoValid={setIsBillingInfoValid}
            setIsCardElementTouched={setIsCardElementTouched}
          />
        </form>
        <Column>
          <h2 className={styles.heading2}>
            {t("CLINIC_CHECKOUT_PAYMENT_SUMMARY")}
          </h2>
          <Terms
            agreement={agreement}
            onChange={onAgreement}
            plan={plan}
            compound={compoundEligible && treatment === COMPOUND}
          />
        </Column>
        <Column className={styles.ctaCtn}>
          {(enrollmentError || error) && errorMessaging && (
            <div>
              <p className={styles.error}>{errorMessaging}</p>
            </div>
          )}
          <RoundButton
            className={cx(styles.checkoutCta, { [styles.pending]: enrolling })}
            onClick={submitPaymentInfo}
            pending={enrolling}
            isDisabled={isCheckoutBtnDisabled}
          >
            {t("CLINIC_CHECKOUT_SUBMIT_CTA")}
          </RoundButton>
          {isCheckoutBtnDisabled && (
            <div className={styles.overlay} onClick={handleOverlayClick} />
          )}
          <RoundButton
            onClick={changePlan}
            className={styles.changePlanCta}
            isDisabled={
              errors?.createSequenceUserIsError ||
              errors?.getSequenceRedirectUrlIsError ||
              enrolling
            }
          >
            {t("CLINIC_CHECKOUT_CHANGE_CTA")}
          </RoundButton>
        </Column>
        {!isUni3or4Quiz && (
          <SequenceFooter className={styles.footer} hideButton={true} />
        )}
      </Column>
    </SiteWrapper>
  );
};

export default Payment;

function getPlanAnalyticsData(plan) {
  return {
    signup_type: plan.isB2b ? B2B : B2C,
    signup_program: plan.subscriptionType?.toLowerCase(),
    signup_offer_bundle_id: plan.bundleId,
    signup_is_activation_path: Boolean(plan.activationCode).toString(),
    signup_offer_plan_id: plan.id,
    signup_offer_plan_name: plan.contentMap?.get(OFFER_PLAN_NAME),
    signup_offer_plan_total: plan.total,
    signup_is_franchise: plan.isFranchise?.toString(),
    signup_subscription_length: plan.intervalLength?.toString(),
    signup_subscription_length_type: plan.contentMap?.get(DURATION_COUNT),
    signup_is_freetrial: plan.isFreeTrial?.toString(),
    signup_commitment_length: plan.totalBillingCycles?.toString(),
    signup_is_ltc: plan.ltc?.toString(),
    pricing_engine_version: RECURLY
  };
}

function getPostPurchaseAnalyticsData(data) {
  const { chargeInvoice: order = {} } = data ?? {};

  return {
    transaction_amount: order.total?.toString(),
    transaction_payment_method: getPaymentType(
      order.transactions && order.transactions[0]?.paymentMethod.cardType
    ),
    transaction_unique_id: order.uuid?.toString(),
    transaction_tax: order.tax?.toString(),
    transaction_discount_code: "",
    transaction_line_items: JSON.stringify(
      order.lineItems?.map(lineItem => ({
        id: lineItem.id,
        name: lineItem.description.toLowerCase(),
        category: lineItem.type,
        price: lineItem.amount,
        quantity: 1 // carried over from signup
      }))
    )
  };
}

function getPaymentType(cardType) {
  return cardType ? `cc-${cardType.toLowerCase().replace(" ", "")}` : "cc";
}

function getUserAndAddressData(data) {
  const { uuid } = getSessionData();

  const { street1, street2, city, state, postalCode, country, email } =
    data || {};

  return {
    user_adr_address_1: street1,
    user_adr_address_2: street2,
    user_adr_city: city,
    user_adr_country: country,
    user_adr_state: state,
    user_adr_zip_code: postalCode,
    user_email: email,
    user_uuid: uuid
  };
}
